/**
 * Commercial opportunity slice (17/06/2026).
 *
 * Cible le retour utilisateur : « les opportunités commerciales donnent un
 * bonus sans contrepartie réaliste — pas d'immobilisation chauffeur+camion ».
 * Désormais, accepter une opportunité (template `cli-new-business-01`) :
 *   1. Vérifie qu'un camion + un chauffeur sont disponibles
 *   2. Les mobilise pendant 3-7 jours
 *   3. Verse le bonus +trésorerie + +réputation UNIQUEMENT à la fin
 *
 * Si la flotte est saturée, l'acceptation est refusée et l'opportunité est
 * marquée comme manquée (−1 réputation, comme un refus standard).
 *
 * Public API:
 *   • `startCommercialOpportunity({ clientName, treasuryReward, reputationReward })`
 *     → { ok: boolean, reason?, vehicleId?, driverId?, endsOnDay? }
 *   • `tickCommercialOpportunities()`         — appelé par coreSlice.nextDay
 *
 * Shape :
 *   commercialOpportunities: [{
 *     id, clientName, startedOnDay, endsOnDay, durationDays,
 *     vehicleId, driverId,
 *     treasuryReward, reputationReward,
 *   }]
 */
import { generateId } from '../helpers';

const MIN_DURATION = 3;
const MAX_DURATION = 7;
const _rollDuration = (rng = Math.random) =>
  MIN_DURATION + Math.floor(rng() * (MAX_DURATION - MIN_DURATION + 1));

export function createCommercialOpportunitySlice(set, get) {
  return {
    commercialOpportunities: [],

    /**
     * Démarre une opportunité commerciale en mobilisant 1 camion + 1 chauffeur
     * disponibles. Retourne { ok: false, reason: 'fleet_saturated' } si aucune
     * paire idle n'est trouvée.
     */
    startCommercialOpportunity: ({ clientName, treasuryReward = 2500, reputationReward = 2, rng } = {}) => {
      const state = get();
      const totalDays = state.totalDays || 0;

      // Trouver un camion idle non assigné.
      const idleVehicle = (state.vehicles || []).find(v =>
        v.status === 'idle'
        && !v.assignedContractId
      );
      if (!idleVehicle) {
        return { ok: false, reason: 'no_vehicle' };
      }

      // Trouver un chauffeur idle disponible (pas en congés/arrêt/grève/formation/mission).
      const idleDriver = (state.drivers || []).find(d =>
        d.status === 'idle'
        && !d.onHoliday
        && !d.assignedVehicleId
      );
      if (!idleDriver) {
        return { ok: false, reason: 'no_driver' };
      }

      const durationDays = _rollDuration(rng || Math.random);
      const opportunity = {
        id: `commercial-opp-${generateId()}`,
        clientName: clientName || 'Client',
        startedOnDay: totalDays,
        endsOnDay: totalDays + durationDays,
        durationDays,
        vehicleId: idleVehicle.id,
        driverId: idleDriver.id,
        treasuryReward,
        reputationReward,
      };

      set({
        commercialOpportunities: [...(state.commercialOpportunities || []), opportunity],
        // Mobilise le binding mais sans contrat (status reste idle car pas de
        // dailyRevenue à comptabiliser ; on garde juste l'occupation visible).
        vehicles: state.vehicles.map(v => v.id === idleVehicle.id ? {
          ...v,
          assignedDriverId: idleDriver.id,
          commercialOpportunityId: opportunity.id,
          status: 'active',
        } : v),
        drivers: state.drivers.map(d => d.id === idleDriver.id ? {
          ...d,
          assignedVehicleId: idleVehicle.id,
          status: 'active',
          commercialOpportunityId: opportunity.id,
        } : d),
      });

      // 04/02/2026 — Q1 Bug Messagerie (retour user) : « Accepter une AO
      // bloque le camion/chauffeur mais rien ne se passe ». L'invisibilité
      // de l'opportunité créait une perception de bug. On pousse désormais
      // un message de confirmation IMMÉDIAT avec date de retour + récompense
      // attendue, pour que l'utilisateur ait un feedback explicite et sache
      // que ses ressources sont bien engagées sur une mission flash payée à
      // la fin.
      if (typeof get().pushCustomMessage === 'function') {
        try {
          get().pushCustomMessage({
            senderType: 'client',
            senderName: opportunity.clientName,
            tone: 'friendly',
            kind: 'info',
            subject: `Mission flash démarrée — retour J+${durationDays}`,
            body:
              `Bonjour,\n\n` +
              `Merci d'avoir accepté notre demande urgente. Votre camion et votre chauffeur ` +
              `sont mobilisés à partir d'aujourd'hui (J${totalDays}) jusqu'au **J${opportunity.endsOnDay}** ` +
              `(${durationDays} jours).\n\n` +
              `**Règlement à la fin de mission : ${(treasuryReward || 0).toLocaleString('fr-FR')} €** ` +
              `(versé sur votre compte au retour). Bonus réputation : +${reputationReward || 0}.\n\n` +
              `Vous retrouverez votre paire camion + chauffeur dans la flotte avec le statut ` +
              `"Mission flash" jusqu'à leur retour.\n\n` +
              `À très vite,\n${opportunity.clientName}`,
            ttlDays: durationDays + 2,
          });
        } catch (err) {
          console.warn('[commercialOpportunity] start confirmation push failed:', err);
        }
      }

      return {
        ok: true,
        opportunityId: opportunity.id,
        vehicleId: idleVehicle.id,
        driverId: idleDriver.id,
        endsOnDay: opportunity.endsOnDay,
        durationDays,
      };
    },

    /**
     * Daily tick — libère les ressources des opportunités terminées et verse
     * la récompense (trésorerie + réputation).
     */
    tickCommercialOpportunities: () => {
      const state = get();
      const totalDays = state.totalDays || 0;
      const opps = state.commercialOpportunities || [];

      // 25/06/2026 hotfix — réconciliation défensive : si un véhicule ou
      // chauffeur reste verrouillé avec un `commercialOpportunityId` qui ne
      // pointe plus vers aucune opportunité active (séquelle du bug de
      // l'overwrite par le set() final), on le libère immédiatement.
      // Une compensation de 2 500 €/binding est créditée à la trésorerie —
      // c'est la valeur médiane d'une opportunité commerciale, on rembourse
      // donc le joueur du paiement perdu.
      const activeOppIds = new Set(opps.map(o => o.id));
      const orphanVehicles = (state.vehicles || []).filter(v => v.commercialOpportunityId && !activeOppIds.has(v.commercialOpportunityId));
      const orphanDrivers = (state.drivers || []).filter(d => d.commercialOpportunityId && !activeOppIds.has(d.commercialOpportunityId));
      // 25/06/2026 (extension) — Bug Lucie Fontaine non visible dans la
      // modal de contrat. Cause probable : driver coincé en `status:
      // 'active'` avec `assignedVehicleId` pointant vers un véhicule qui
      // n'est plus actif (séquelle d'un ancien bug d'orphan ou
      // d'incohérence d'état). On unsticke aussi ces drivers/vehicles :
      // si la liaison ne correspond à aucun contrat ou opportunité en
      // cours, on libère les deux côtés.
      const activeContractIds = new Set((state.activeContracts || []).map(c => c.instanceId));
      const stuckVehicles = (state.vehicles || []).filter(v => {
        if (orphanVehicles.includes(v)) return false; // déjà traité
        if (v.status !== 'active') return false;
        // Active mais sans contrat ni opportunité valide → orphan.
        const hasValidContract = v.assignedContractId && activeContractIds.has(v.assignedContractId);
        const hasValidOpp = v.commercialOpportunityId && activeOppIds.has(v.commercialOpportunityId);
        return !hasValidContract && !hasValidOpp;
      });
      const stuckDrivers = (state.drivers || []).filter(d => {
        if (orphanDrivers.includes(d)) return false;
        if (d.status !== 'active') return false;
        if (!d.assignedVehicleId) return true; // active sans véhicule = orphan
        const v = (state.vehicles || []).find(vv => vv.id === d.assignedVehicleId);
        if (!v) return true; // pointe sur véhicule disparu
        const hasValidContract = v.assignedContractId && activeContractIds.has(v.assignedContractId);
        const hasValidOpp = v.commercialOpportunityId && activeOppIds.has(v.commercialOpportunityId);
        return !hasValidContract && !hasValidOpp;
      });
      const orphanCount = Math.max(orphanVehicles.length, orphanDrivers.length);
      const stuckCount = Math.max(stuckVehicles.length, stuckDrivers.length);
      if (orphanCount > 0 || stuckCount > 0) {
        const compensation = orphanCount * 2500; // pas de compensation pour les "stuck" sans opp
        const orphanIds = new Set([...orphanVehicles.map(v => v.id), ...stuckVehicles.map(v => v.id)]);
        const orphanDIds = new Set([...orphanDrivers.map(d => d.id), ...stuckDrivers.map(d => d.id)]);
        set({
          vehicles: state.vehicles.map(v => orphanIds.has(v.id) ? {
            ...v, status: 'idle', assignedDriverId: null, assignedContractId: null, commercialOpportunityId: null,
          } : v),
          drivers: state.drivers.map(d => orphanDIds.has(d.id) ? {
            ...d, status: 'idle', assignedVehicleId: null, commercialOpportunityId: null,
          } : d),
          treasury: (state.treasury || 0) + compensation,
          notifications: compensation > 0
            ? [...(state.notifications || []).slice(-30), {
                id: generateId(),
                type: 'commercial_opportunity_recovery',
                title: 'Récupération mission commerciale',
                message: `${orphanCount} ressource${orphanCount > 1 ? 's' : ''} libérée${orphanCount > 1 ? 's' : ''} après mission. Compensation versée : +${compensation.toLocaleString('fr-FR')} €.`,
                day: totalDays,
                read: false,
              }]
            : (stuckCount > 0
                ? [...(state.notifications || []).slice(-30), {
                    id: generateId(),
                    type: 'stuck_resource_recovery',
                    title: 'Ressources réconciliées',
                    message: `${stuckCount} ressource${stuckCount > 1 ? 's' : ''} libérée${stuckCount > 1 ? 's' : ''} : assignation incohérente nettoyée.`,
                    day: totalDays,
                    read: false,
                  }]
                : (state.notifications || [])),
        });
      }

      if (opps.length === 0) return;

      const completing = opps.filter(o => totalDays >= o.endsOnDay);
      if (completing.length === 0) return;

      const completingIds = new Set(completing.map(o => o.id));
      const releasedVehicleIds = new Set(completing.map(o => o.vehicleId));
      const releasedDriverIds = new Set(completing.map(o => o.driverId));

      const treasuryGain = completing.reduce((s, o) => s + (o.treasuryReward || 0), 0);
      const repGain = completing.reduce((s, o) => s + (o.reputationReward || 0), 0);

      // Notifications + email récapitulatif par opportunité.
      const newNotifications = [...(state.notifications || []).slice(-30)];
      completing.forEach(o => {
        newNotifications.push({
          id: generateId(),
          type: 'commercial_opportunity_done',
          title: 'Opportunité commerciale terminée',
          message: `${o.clientName} : mission de ${o.durationDays} j conclue. +${(o.treasuryReward || 0).toLocaleString('fr-FR')} € · +${o.reputationReward || 0} réputation.`,
          day: totalDays,
          read: false,
        });
      });

      // Email inbox (uniquement si pushCustomMessage existe — pas en tests unit).
      if (typeof get().pushCustomMessage === 'function') {
        completing.forEach(o => {
          try {
            get().pushCustomMessage({
              senderType: 'client',
              senderName: o.clientName,
              tone: 'friendly',
              kind: 'info',
              subject: 'Merci pour la prestation',
              body: `Bonjour,\n\nLa mission urgente confiée à vos équipes s'est très bien déroulée sur ${o.durationDays} jours. Camion et chauffeur ont été irréprochables.\n\nVous trouverez le règlement de ${(o.treasuryReward || 0).toLocaleString('fr-FR')} € sur votre compte.\n\nÀ très vite pour de nouvelles collaborations.\n\n${o.clientName}`,
              ttlDays: 10,
            });
          } catch (err) {
            console.warn('[commercialOpportunity] inbox push failed:', err);
          }
        });
      }

      set({
        commercialOpportunities: opps.filter(o => !completingIds.has(o.id)),
        treasury: (state.treasury || 0) + treasuryGain,
        reputation: Math.max(0, Math.min(100, (state.reputation || 0) + repGain)),
        vehicles: state.vehicles.map(v => releasedVehicleIds.has(v.id) ? {
          ...v,
          status: 'idle',
          assignedDriverId: null,
          commercialOpportunityId: null,
        } : v),
        drivers: state.drivers.map(d => releasedDriverIds.has(d.id) ? {
          ...d,
          status: 'idle',
          assignedVehicleId: null,
          commercialOpportunityId: null,
        } : d),
        notifications: newNotifications,
      });
    },

    /**
     * Sélecteur UI — opportunités actives.
     */
    getActiveCommercialOpportunities: () => get().commercialOpportunities || [],
  };
}
